import { useEffect, useState } from 'react';
import { fetchTripDetail, type UiTripDetail } from '../api';
import { hhmm } from '../format';

interface Props {
  tripUid: string;
  serviceDate: string;
  onShape: (shape: [number, number][] | null) => void;
  onClose: () => void;
}

const REFRESH_MS = 15_000;

export function TripSheet({ tripUid, serviceDate, onShape, onClose }: Props) {
  const [data, setData] = useState<UiTripDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stopped = false;
    const ctrl = new AbortController();
    const load = async () => {
      try {
        const res = await fetchTripDetail(tripUid, serviceDate, ctrl.signal);
        if (stopped) return;
        setData(res);
        setError(null);
        onShape(res.shape);
      } catch (err) {
        if (stopped || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError('便情報を取得できませんでした');
      }
    };
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => {
      stopped = true;
      ctrl.abort();
      clearInterval(t);
      onShape(null);
    };
  }, [tripUid, serviceDate, onShape]);

  return (
    <section className="sheet" aria-label="便詳細">
      <header className="sheet-head">
        <div>
          <h2 className="sheet-title">
            {data?.routeShortName && <span className="route-badge route-badge-lg">{data.routeShortName}</span>}
            {data ? `${data.headsign} 行き` : '読み込み中…'}
          </h2>
          <p className="sheet-sub">
            {data?.operatorName}
            {data && <VehicleBadge hasRealtimeVehicle={data.hasRealtimeVehicle} />}
          </p>
        </div>
        <button className="close-btn" onClick={onClose} aria-label="戻る">‹ 戻る</button>
      </header>

      <div className="sheet-body">
        {error && <p className="error">{error}</p>}
        {data && (
          <ol className="trip-stops">
            {data.stops.map((s) => {
              const time = s.expectedDeparture ?? s.expectedArrival ?? s.scheduledDeparture ?? s.scheduledArrival;
              return (
                <li
                  key={`${s.stopUid}-${s.seq}`}
                  className={`trip-stop ${s.passed ? 'passed' : ''} ${s.cancelled ? 'cancelled' : ''}`}
                >
                  <span className="trip-line">
                    <span className={`trip-node ${s.passed ? 'trip-node-passed' : ''}`} />
                  </span>
                  <span className="trip-time">{hhmm(time)}</span>
                  <span className="trip-name">
                    {s.name}
                    {s.cancelled && <em className="cancel-tag">通過/運休</em>}
                    {s.passed && s.passedBasis === 'inferred' && <em className="inferred-tag">推定</em>}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}

function VehicleBadge({ hasRealtimeVehicle }: { hasRealtimeVehicle: boolean }) {
  if (hasRealtimeVehicle) return <span className="v-badge v-rt"> · 車両位置: リアルタイム</span>;
  return <span className="v-badge v-none"> · 車両位置なし（時刻表ベース）</span>;
}
